import { MapMarker, SearchResult } from 'state/ducks/locationUnit/models'
import { Filter } from 'state/ducks/space/models'

type SearchParams = {
  latitude: number | string
  longitude: number | string
  booked_from: string
  booked_to: string
  large_locker_num: number
  small_locker_num: number
  has_wifi?: boolean
  has_charge?: boolean
  available_in_english?: boolean
  no_smoking?: boolean
  has_elevator?: boolean
  available_24_hour?: boolean
}

export function mapFilterToParams(filter: Filter): SearchParams {
  const params: SearchParams = {
    latitude: filter.lat,
    longitude: filter.lon,
    booked_from: filter.booked_from,
    booked_to: filter.booked_to,
    large_locker_num: filter.suitcase_size,
    small_locker_num: filter.bag_size,
  }

  const keys = [
    'has_wifi',
    'has_charge',
    'available_in_english',
    'no_smoking',
    'has_elevator',
    'available_24_hour',
  ] as const

  keys.forEach((key) => {
    if (filter[key]) {
      params[key] = true
    }
  })

  return params
}

// Location
export type ApiLocation = {
  latitude: number
  longitude: number
}

// BasicInfo
export type ApiBasicInfo = {
  has_elevator: boolean
  can_book_on_the_day: boolean
  available_24_hour: boolean
  has_charge: boolean
  no_smoking: boolean
  available_in_english: boolean
  has_wifi: boolean
}

// Space
export type ApiSpace = {
  type: 'space'
  location: ApiLocation
  /** v2: canonical /spaces 用 UUID */
  id?: string
  name: string
  encrypted_id: string
  en_name: string
  main_image_url: string
  small_locker_num: number
  large_locker_num: number
  suitcase_size: number
  average_rating: number
  sub_category_id: number
  bag_size: number
  space_type: number
  is_available: boolean
  basic_info: ApiBasicInfo
  map_marker?: MapMarker | null
}

// LockerCluster
export type ApiLockerCluster = {
  type: 'locker_cluster' | 'pudo'
  location: ApiLocation
  /** v2: canonical /spaces 用 UUID */
  id?: string
  encrypted_id: string
  name: string
  en_name: string
  main_image_url: string
  small_locker_num: number
  medium_locker_num: number
  large_locker_num: number
  average_rating: number
  small_locker_price: number
  medium_locker_price: number
  large_locker_price: number
  is_available: boolean
  basic_info: ApiBasicInfo
}

// SearchResult
export type ApiSearchResult = {
  locationUnits: (ApiSpace | ApiLockerCluster)[]
  total: number
}

export function mapSearchResponseToSearchResult(response: ApiSearchResult): SearchResult {
  return {
    locationUnits: response.locationUnits.map((unit: ApiSpace | ApiLockerCluster) => {
      if (unit.type === 'space') {
        return {
          type: 'space',
          location: {
            latitude: unit.location.latitude,
            longitude: unit.location.longitude,
          },
          id: unit.id,
          encryptedId: unit.encrypted_id,
          spaceType: unit.space_type,
          name: unit.name,
          enName: unit.en_name,
          mainImageUrl: unit.main_image_url,
          smallLockerNum: unit.small_locker_num,
          largeLockerNum: unit.large_locker_num,
          suitcaseSize: unit.suitcase_size,
          averageRating: unit.average_rating,
          subCategoryId: unit.sub_category_id,
          bagSize: unit.bag_size,
          isAvailable: unit.is_available,
          mapMarker: unit.map_marker ?? null,
          basicInfo: {
            hasElevator: unit.basic_info.has_elevator,
            canBookOnTheDay: unit.basic_info.can_book_on_the_day,
            available24Hour: unit.basic_info.available_24_hour,
            hasCharge: unit.basic_info.has_charge,
            noSmoking: unit.basic_info.no_smoking,
            availableInEnglish: unit.basic_info.available_in_english,
            hasWifi: unit.basic_info.has_wifi,
          },
        }
      } else {
        // locker_cluster or pudo
        const mappedType = unit.type === 'pudo' ? 'pudo_locker' : 'locker_cluster'
        return {
          type: mappedType,
          location: {
            latitude: unit.location.latitude,
            longitude: unit.location.longitude,
          },
          id: unit.id,
          encryptedId: unit.encrypted_id,
          name: unit.name,
          enName: unit.en_name,
          mainImageUrl: unit.main_image_url,
          smallLockerNum: unit.small_locker_num,
          mediumLockerNum: unit.medium_locker_num,
          largeLockerNum: unit.large_locker_num,
          averageRating: unit.average_rating,
          smallLockerPrice: unit.small_locker_price,
          mediumLockerPrice: unit.medium_locker_price,
          largeLockerPrice: unit.large_locker_price,
          isAvailable: unit.is_available,
          basicInfo: {
            hasElevator: unit.basic_info.has_elevator,
            canBookOnTheDay: unit.basic_info.can_book_on_the_day,
            available24Hour: unit.basic_info.available_24_hour,
            hasCharge: unit.basic_info.has_charge,
            noSmoking: unit.basic_info.no_smoking,
            availableInEnglish: unit.basic_info.available_in_english,
            hasWifi: unit.basic_info.has_wifi,
          },
        }
      }
    }),
    total: response.total,
  }
}
